import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, Modal, Row, Table } from "react-bootstrap";
import DataTable from "react-data-table-component";
import moment from "moment";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import DataTableSkeleton from "../../components/DataTableSkeleton";
import AdminEmptyState from "../../components/admin/AdminEmptyState";
import {
  formatCurrency,
  getAdminAffiliatePayoutProfile,
  getAdminCommissions,
  getAdminPayoutDetails,
  getAdminPayouts,
  normalizeCommissionList,
  normalizePagination,
  normalizePayoutList,
} from "../../helper/affiliate_helper";
import { dataTableCustomStyles, dataTablePaginationOptions } from "../../components/admin/dataTableConfig";
import AdminRowActions, { AdminActionButton } from '../../components/admin/AdminRowActions';

const getPayoutId = (row) => row?.id || row?._id || null;

const getAffiliateLabel = (row) =>
  row?.affiliate?.name ||
  row?.affiliateName ||
  row?.affiliate?.email ||
  row?.affiliateEmail ||
  row?.affiliateId ||
  "—";

const formatMethod = (method) => {
  if (method === "bank_transfer") return "Bank transfer";
  if (method === "upi") return "UPI";
  return method || "—";
};

function PayoutDetailContent({ payout, loading }) {
  if (loading) return <p className="text-muted mb-0">Loading payout details…</p>;
  if (!payout) return <p className="text-muted mb-0">No details available.</p>;

  const snapshot = payout.payoutDetailsSnapshot || {};

  return (
    <div>
      <Table bordered responsive className="mb-3">
        <tbody>
          <tr><th style={{ width: "200px" }}>Payout ID</th><td><code className="small">{getPayoutId(payout)}</code></td></tr>
          <tr><th>Affiliate ID</th><td>{payout.affiliateId || "—"}</td></tr>
          <tr><th>Amount</th><td>{formatCurrency(payout.totalAmount, payout.currency)}</td></tr>
          <tr><th>Method</th><td>{formatMethod(payout.payoutMethod)}</td></tr>
          <tr><th>UTR / Reference</th><td>{payout.transactionReference || "—"}</td></tr>
          <tr><th>Status</th><td><Badge bg="success">{payout.status || "completed"}</Badge></td></tr>
          <tr><th>Date</th><td>{payout.processedAt || payout.createdAt ? moment(payout.processedAt || payout.createdAt).format("DD MMM YYYY, hh:mm A") : "—"}</td></tr>
          {payout.notes && <tr><th>Notes</th><td>{payout.notes}</td></tr>}
        </tbody>
      </Table>

      {Object.keys(snapshot).length > 0 && (
        <>
          <h6 className="mb-2">Payment snapshot</h6>
          <Table bordered responsive size="sm" className="mb-3">
            <tbody>
              {Object.entries(snapshot).map(([key, value]) => (
                <tr key={key}>
                  <th style={{ width: "200px" }}>{key}</th>
                  <td>{value ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {Array.isArray(payout.commissions) && payout.commissions.length > 0 && (
        <>
          <h6 className="mb-2">Linked commissions</h6>
          <Table bordered responsive size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Order</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {payout.commissions.map((item) => (
                <tr key={item.id || item._id}>
                  <td>{item.orderNumber || item.orderId || "—"}</td>
                  <td>{formatCurrency(item.commissionAmount, item.currency)}</td>
                  <td>{item.status || "—"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {Array.isArray(payout.commissionIds) && payout.commissionIds.length > 0 && !payout.commissions?.length && (
        <p className="text-muted small mb-0">
          Commission IDs: {payout.commissionIds.join(", ")}
        </p>
      )}
    </div>
  );
}

export default function AffiliatePayoutHistory() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [affiliateId, setAffiliateId] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    const id = searchParams.get("affiliateId");
    if (id) setAffiliateId(id);
  }, [searchParams]);

  const fetchPayouts = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (affiliateId.trim()) params.affiliateId = affiliateId.trim();

      const result = await getAdminPayouts(params);
      const list = normalizePayoutList(result);
      const pagination = normalizePagination(result);

      if (result?.success === false && !list.length) {
        toast.error(result?.message || "Failed to load payout history");
        setData([]);
        setTotalRecords(0);
        return;
      }

      setData(list);
      setTotalRecords(pagination?.total ?? list.length);
    } catch (error) {
      if (error?.status !== 401) {
        toast.error(error?.message || "Failed to load payout history");
      }
      setData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayouts();
  }, [page, limit, affiliateId]);

  useEffect(() => {
    const next = {};
    if (affiliateId.trim()) next.affiliateId = affiliateId.trim();
    setSearchParams(next, { replace: true });
  }, [affiliateId, setSearchParams]);

  const openDetail = async (row) => {
    const payoutId = getPayoutId(row);
    if (!payoutId) return;

    setSelected(row);
    setDetail(row);
    setDetailLoading(true);
    try {
      const res = await getAdminPayoutDetails(payoutId);
      if (res?.success === false) {
        toast.error(res?.message || "Failed to load payout details");
        return;
      }
      setDetail({
        ...(res?.data?.payout || res?.data || row),
        commissions: res?.data?.commissions || [],
      });
    } catch (error) {
      if (error?.status !== 401) {
        toast.error(error?.message || "Failed to load payout details");
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = useMemo(() => [
    {
      name: "Date",
      selector: (row) => (row.processedAt || row.createdAt ? moment(row.processedAt || row.createdAt).valueOf() : 0),
      sortable: true,
      minWidth: "140px",
      cell: (row) =>
        row.processedAt || row.createdAt
          ? moment(row.processedAt || row.createdAt).format("DD MMM YYYY, hh:mm A")
          : "—",
    },
    {
      name: "Affiliate",
      minWidth: "160px",
      cell: (row) => getAffiliateLabel(row),
    },
    {
      name: "Amount",
      selector: (row) => row.totalAmount ?? 0,
      sortable: true,
      minWidth: "120px",
      cell: (row) => formatCurrency(row.totalAmount, row.currency),
    },
    {
      name: "Method",
      selector: (row) => row.payoutMethod,
      minWidth: "120px",
      cell: (row) => formatMethod(row.payoutMethod),
    },
    {
      name: "UTR",
      selector: (row) => row.transactionReference || "—",
      minWidth: "160px",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      minWidth: "100px",
      cell: (row) => <Badge bg="success">{row.status || "completed"}</Badge>,
    },
    {
      name: "Action",
      minWidth: "80px",
      ignoreRowClick: true,
      cell: (row) => (
        <AdminActionButton action="view" label="View payout" onClick={() => openDetail(row)} />
      ),
    },
  ], []);

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/admin/dashboard">Dashboard</Link></li>
              <li className="breadcrumb-item active" aria-current="page">Affiliate Payouts</li>
            </ol>
            <h4 className="main-title mb-0 admin-page-title">Payout History</h4>
            <p className="text-secondary small mb-0 mt-1">Manual UPI and bank transfer records</p>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <Link to="/affiliate-payouts/new" className="btn btn-primary">
              <i className="ri-add-line me-1" />
              Record payout
            </Link>
            <Button variant="outline-primary" onClick={fetchPayouts} disabled={loading}>
              <i className={`ri-refresh-line me-1 ${loading ? "spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        <Card className="border-0 shadow-sm mb-3">
          <Card.Body>
            <Row className="g-2 align-items-end">
              <Col md={4} sm={8}>
                <Form.Label className="small text-muted mb-1">Affiliate ID</Form.Label>
                <Form.Control
                  value={affiliateId}
                  onChange={(e) => { setAffiliateId(e.target.value); setPage(1); }}
                  placeholder="Filter by affiliate ID"
                />
              </Col>
              <Col md={2} sm={4}>
                <Button
                  variant="outline-secondary"
                  className="w-100"
                  onClick={() => { setAffiliateId(""); setPage(1); }}
                >
                  Clear
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="border-0 shadow-sm">
          <Card.Body>
            {loading ? (
              <DataTableSkeleton rows={6} columns={7} />
            ) : data.length === 0 ? (
              <AdminEmptyState
                icon="ri-bank-card-line"
                title="No payouts recorded yet"
                description="Record a manual payout after paying an affiliate externally."
              />
            ) : (
              <DataTable
                columns={columns}
                data={data}
                customStyles={dataTableCustomStyles}
                pagination
                paginationServer
                paginationTotalRows={totalRecords}
                paginationDefaultPage={page}
                onChangePage={setPage}
                paginationPerPage={limit}
                paginationComponentOptions={dataTablePaginationOptions}
                highlightOnHover
              />
            )}
          </Card.Body>
        </Card>

        <Modal show={Boolean(selected)} onHide={() => { setSelected(null); setDetail(null); }} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Payout Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <PayoutDetailContent payout={detail} loading={detailLoading} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => { setSelected(null); setDetail(null); }}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <Footer />
      </div>
    </React.Fragment>
  );
}
